import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface Infographic {
  id: string;
  title: string;
  img: string | null;
  pdfUrl: string | null;
  filterType: string | null;
  isActive: boolean;
  companyId: string;
  destinationId?: string;
}

interface InfographicApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: Infographic[];
}

// ─── Server-side Data Fetching ───────────────────────────────────────

export const fetchInfographics = cache(
  async (
    page = 1,
    pageSize = 8,
    filterType?: string,
    destinationId?: string,
  ): Promise<{
    infographics: Infographic[];
    totalPages: number;
    count: number;
  }> => {
    const res = await api.get<InfographicApiResponse>(ApiRoutes.WEBSITE_INFOGRAPHICS, {
      params: {
        PageNumber: page,
        PageSize: pageSize,
        ...(filterType ? { FilterType: filterType } : {}),
        ...(destinationId ? { DestinationId: destinationId } : {}),
      },
    });

    const infographics = res.data?.data ?? [];
    return {
      infographics,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

export const fetchInfographicFilterTypes = cache(
  async (): Promise<string[]> => {
    const res = await api.get<InfographicApiResponse>(ApiRoutes.WEBSITE_INFOGRAPHICS, {
      params: { PageNumber: 1, PageSize: 100000 },
    });
    const types: string[] = [];
    for (const item of res.data?.data ?? []) {
      if (
        item.filterType &&
        item.filterType.length > 0 &&
        !types.includes(item.filterType)
      ) {
        types.push(item.filterType);
      }
    }
    return types;
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchInfographicsClient(
  page = 1,
  pageSize = 8,
  filterType?: string,
  destinationId?: string,
): Promise<{
  infographics: Infographic[];
  totalPages: number;
  count: number;
}> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
  });
  if (filterType) params.set("FilterType", filterType);
  if (destinationId) params.set("DestinationId", destinationId);

  const res = await fetch(`${BASE_URL}/website/infographics?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const infographics: Infographic[] = json.data ?? [];
  return {
    infographics,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
